import { useEffect, useState } from 'react';

import { type AxiosRequestConfig } from 'axios';

import { Col, Container, Footer, Row, Header, Typography } from 'darrius';

import {
  HeadContent,
  Toast,
  BundlesMercadoPago,
  GamificationCard,
} from '../../components';
import { showToast } from '../../components/atoms/Toast/Toast';

import { customTracking } from '../../utils/customTracking';
import { axiosInstance } from '../../utils/axiosInstance';
import { navigate } from '../../utils/navigate';
import { hasLockedPixGamification } from '../../utils/pendingPixGamification';
import { saveStoreEntryState } from '../../utils/storeEntryState';

import {
  IGamificationInfo,
  type IWebCheckoutTemplateProps,
} from './@interfaces';
import {
  CheckoutContext,
  type ICheckoutData,
  initialValue,
} from '../../context/Checkout';

import styles from './WebCheckoutTemplateMercadoPago.module.scss';

declare global {
  interface Window {
    newrelic?: any;
    NREUM?: any;
  }
}

export default function WebCheckoutTemplateMercadoPago({
  staticContent,
  profile,
  authToken,
  bundlesData,
  paymentCards,
  isCreditCardMpActive,
  isPixMpActive,
  userState,
  isPagaLeveActive,
  userInfo,
  changePagarmeToPagaleve,
  newDesign,
  isIuguActive,
  isStarkBankActive,
  isPagzoopActive,
  gamificationInfo,
  hasLockedPix: hasLockedPixInitial = false,
}: IWebCheckoutTemplateProps) {
  const { footerContent } = staticContent;
  const [checkoutData, setCheckoutData] = useState<ICheckoutData>(initialValue);
  const [gamificationData, setGamificationData] =
    useState<IGamificationInfo>(gamificationInfo);
  const [hasLockedPix, setHasLockedPix] = useState(hasLockedPixInitial);

  useEffect(() => {
    setHasLockedPix(hasLockedPixGamification());
  }, [gamificationData]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = '/seja-um-profissional/js/newrelic.js';
    script.async = true;
    script.type = 'text/javascript';
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
      delete window.NREUM;
      delete window.newrelic;
    };
  }, []);

  useEffect(() => {
    setCheckoutData((prev) => {
      return { ...prev, profile, bundlesData, authToken };
    });

    // Guarda profileId e userState do momento em que o usuário entrou na loja
    // de moedas, para comparar com o userState enviado na compra. Ver
    // logBundlePrice em CreditCardView/PixView/BoletoView.
    saveStoreEntryState({ profileId: profile.id, userState });

    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;

      if (window?.newrelic) {
        window.newrelic.addPageAction('webcheckout_action', {
          step: 'screen_load',
          profile_id: profile.id,
          origin: 'webcheckout',
          store_type: 'GN_STORE',
          context_type: 'three_bundle_store_3_0',
        });
        clearInterval(interval);
      } else if (attempts >= 50) {
        clearInterval(interval);
      }
    }, 100);

    customTracking({
      schema: 'iglu:br.com.getninjas/purchase_screen_load/jsonschema/1-0-1',
      data: {
        profile_id: profile.id,
        store_type: 'GN_STORE',
        context_type: 'three_bundle_store_3_0',
      },
    });

    return () => clearInterval(interval);
  }, []);

  async function handleLogoutUser() {
    try {
      const requestOptions: AxiosRequestConfig = {
        url: `${process.env.NEXT_PUBLIC_API_PRO}/logout`,
        method: 'POST',
        data: {
          authToken,
        },
      };

      await axiosInstance(requestOptions);
      navigate('/seja-um-profissional/login');
    } catch (error) {
      showToast({ message: error.response.data.clientMessage, type: 'error' });
    }
  }

  const isEligible = gamificationData?.eligible || false;

  return (
    <CheckoutContext.Provider
      value={{
        checkoutData,
        updateCheckoutData: setCheckoutData,
      }}
    >
      <HeadContent
        metaTitle="GetNinjas | Orçamento de Profissionais Confiáveis no GetNinjas.com.br"
        slug="web checkout"
        metaDescription="GetNinjas - Checkout"
        type="prospect:web-checkout"
      />

      <Header.Root>
        <Header.NavMenu>
          <Header.Auth
            isLogged={!!checkoutData}
            onLogout={handleLogoutUser}
            user={{
              avatar: checkoutData.profile.name,
              name: checkoutData.profile.name,
              phone: checkoutData.profile.phone,
            }}
          />
        </Header.NavMenu>
      </Header.Root>

      <Container className={styles['web-checkout__container']}>
        <Row className="space-top center-sm">
          <Col>
            <Typography.Title size="3xl">Escolha seu pacote:</Typography.Title>
          </Col>
        </Row>

        {isEligible && (
          <Row className="space-top center-sm">
            <Col className="col-md-12 col-sm-11">
              <GamificationCard gamificationData={gamificationData} />
            </Col>
          </Row>
        )}

        {hasLockedPix && (
          <Row className="space-top center-sm">
            <Col className="col-md-12 col-sm-11">
              <div className={styles['pix-locked-banner']}>
                <Typography.Text>
                  Há um PIX em aberto com o desconto da sequência aplicado. Os
                  descontos voltam a ficar disponíveis após o pagamento ou
                  expiração do código (até 24h).
                </Typography.Text>
              </div>
            </Col>
          </Row>
        )}

        <Row className="space-top center-sm">
          <Col className="col-md-12 col-sm-11">
            <BundlesMercadoPago
              appButton={true}
              origin={'webcheckout'}
              paymentCards={paymentCards}
              isCreditCardMpActive={isCreditCardMpActive}
              isPixMpActive={isPixMpActive}
              userState={userState}
              isPagaLeveActive={isPagaLeveActive}
              userInfo={userInfo}
              changePagarmeToPagaleve={changePagarmeToPagaleve}
              newDesign={newDesign}
              isIuguActive={isIuguActive}
              isStarkBankActive={isStarkBankActive}
              isPagzoopActive={isPagzoopActive}
              gamificationInfo={gamificationData}
              setGamificationData={setGamificationData}
            />
          </Col>
        </Row>

        <Row className="space-top space-bottom center-sm">
          <Col>
            <Typography.Text className={styles.text}>
              🔒 Segurança garantida dos seus dados bancários
            </Typography.Text>
            <img
              src={`${process.env.NEXT_PUBLIC_ASSETS_PREFIX}/images/purchase-icons.svg`}
              alt="Imagem de metodos de pagamentos e bandeiras de cartões de crédito"
            />
          </Col>
        </Row>
      </Container>

      <Footer data={footerContent} />
      <Toast />
    </CheckoutContext.Provider>
  );
}
